// Story 8.5 (ADO-2014), AC1/AC4, D-11, D-15 and D-19: the cut-off status for a cart, read
// from the shared `useCutOffStore` (one read and one set of refetch timers per cart,
// however many consumers). Screens call `useDepotCutOff`, which adds the countdown; this is the
// status half on its own.
//
//   const cutOff = useCutOffStatus(cart);   // { status, offsetMs } | null
//
// Story 8.4 (ADO-2013), 8.5 D-20: the cart is the depot AND the Trading Account
// (`CartAccount`), since each Value Centre account has its own cut-off; a cart with no account
// chosen names none. A new object for the same cart is the same subscription (it is keyed by
// `cartAccountKey`, so Chill and Fruit & Veg share one; Enabler E8-G, ADO-3109), so callers need
// not memoise it.
//
// Behaviour (the `useFreeDeliveryStatus` template, now shared):
//   - It subscribes to the cart on mount and on a change of cart, and unsubscribes on a
//     switch or unmount; the last consumer to leave aborts the read in flight one tick
//     later, unless a consumer of the same cart joins first (a navigation, StrictMode).
//   - It returns `null` ("hidden") while the first answer for a cart loads, when that
//     first read fails, once a failed refetch has used up its retries (D-19), and with no
//     cart. `NONE` is returned as-is; the components render nothing for it. During a
//     same-cart refetch, and while a failed refetch is retried, the last answer stays up.
//   - The refetch and retry rules (the deadline, Dublin midnight, D-19) and `offsetMs` are
//     the store's.

import { useEffect, useRef } from 'react';
import { cartAccountKey, type CartAccount } from '../api/cart-target';
import { subscribeToCartCutOff, useCutOffStore, type CutOffStatusView } from './useCutOffStore';

export type { CutOffStatusView } from './useCutOffStore';

export function useCutOffStatus(
  cart: CartAccount | null,
  options: { readonly now?: () => number } = {},
): CutOffStatusView | null {
  const now = options.now ?? Date.now;
  const nowRef = useRef(now);
  nowRef.current = now;
  const cartKey = cart === null ? null : cartAccountKey(cart);
  // The latest render's cart (same key as `cartKey`), read by the effect below, which re-runs on
  // the key alone so a new object for the same cart does not resubscribe.
  const cartRef = useRef(cart);
  cartRef.current = cart;

  useEffect(() => {
    const current = cartRef.current;
    if (cartKey === null || current === null) return;
    return subscribeToCartCutOff(current, () => nowRef.current());
  }, [cartKey]);

  return useCutOffStore((state) => (cartKey === null ? null : (state.views[cartKey] ?? null)));
}
